import React, { useCallback } from 'react';
import { ActivityIndicator, Pressable, type StyleProp, type ViewStyle } from 'react-native';
import { useThemeColors } from '../hooks/useThemeColors';
import { useHaptics } from '../hooks/useHaptics';
import { useHitSlop } from '../utils/useHitSlop';
import { Icon, type IconName, type IconTone } from './Icon';

export type IconButtonSize = 'sm' | 'md';

const SIZES: Record<IconButtonSize, { box: number; icon: number }> = {
  sm: { box: 36, icon: 18 },
  md: { box: 44, icon: 24 },
};

export interface IconButtonProps {
  iconName: IconName;
  onPress: () => void;
  /**
   * Required. WCAG 4.1.2 — every interactive control with a non-text
   * label must announce a name. Type-enforced.
   */
  accessibilityLabel: string;
  /** sm = 36px (peripheral actions), md = 44px (primary action). Default: 'sm'. */
  size?: IconButtonSize;
  /** Semantic icon tone — resolves via `useThemeColors`. Default: `default`. */
  tone?: IconTone;
  /** Background color override (for active/raised treatments). */
  background?: string;
  /** Foreground (icon color) override. Wins over `tone`. */
  foreground?: string;
  disabled?: boolean;
  loading?: boolean;
  /**
   * Toggle-state for buttons that act like switches (e.g., a Bold
   * button in a formatting toolbar). Maps to `accessibilityState.selected`
   * + `aria-pressed` so screen readers announce the toggle state.
   * Omit for plain action buttons.
   */
  selected?: boolean;
  style?: StyleProp<ViewStyle>;
  /** Accessibility role override. Defaults to 'button'. */
  accessibilityRole?: 'button' | 'link';
  /** Test-id passthrough. */
  testID?: string;
}

/**
 * Standardized circular icon-tappable. Generalizes `CardActionButton`'s
 * pattern across the app: enforces the 44pt touch-target floor via
 * `useHitSlop`, requires `accessibilityLabel` at the type level, and
 * paints loading / disabled via Phase 1 tokens.
 *
 * Composition: wraps `Pressable` + `Icon`. Callers must not reach for
 * `MaterialIcons` directly — that import becomes a lint error in
 * Phase 6.
 */
export function IconButton({
  iconName,
  onPress,
  accessibilityLabel,
  size = 'sm',
  tone = 'default',
  background,
  foreground,
  disabled = false,
  loading = false,
  selected,
  style,
  accessibilityRole = 'button',
  testID,
}: IconButtonProps) {
  const theme = useThemeColors();
  const haptics = useHaptics();
  const dims = SIZES[size];
  const hitSlop = useHitSlop(dims.box);
  const isBlocked = disabled || loading;

  const handlePress = useCallback(() => {
    // Light tap on every IconButton press — affordance is small, so
    // tactile confirmation that the input registered is valuable.
    haptics.light();
    onPress();
  }, [haptics, onPress]);

  const resolvedBg = background ?? 'transparent';
  // disabled / loading dim via theme.disabledOpacity (ADR-0072).
  const opacity = disabled ? theme.disabledOpacity : 1;

  return (
    <Pressable
      onPress={isBlocked ? undefined : handlePress}
      disabled={isBlocked}
      hitSlop={hitSlop}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      accessibilityState={{
        disabled,
        busy: loading,
        ...(selected !== undefined ? { selected } : null),
      }}
      // Web mirrors: aria-disabled / aria-busy / aria-pressed.
      aria-disabled={disabled || undefined}
      aria-busy={loading || undefined}
      aria-pressed={selected !== undefined ? selected : undefined}
      style={({ pressed }) => [
        {
          width: dims.box,
          height: dims.box,
          borderRadius: dims.box / 2,
          backgroundColor: resolvedBg,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: pressed && !isBlocked ? theme.pressOpacity * opacity : opacity,
        },
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={foreground ?? theme.textOnSurface} />
      ) : (
        <Icon
          name={iconName}
          size={dims.icon}
          {...(foreground ? { color: foreground } : { tone })}
        />
      )}
    </Pressable>
  );
}
